import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PasswordChangeForm } from '@/components/Users/PasswordChangeForm';
import { UserProfile } from '@/hooks/useProfile';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { maskEmail, maskPhoneNumber } from '@/utils/demoMode';
import { formatPhoneNumber } from '@/lib/utils';
import { keycloakApi } from '@/integrations/keycloak/client';
import { useToast } from '@/hooks/use-toast';

const profileSchema = z.object({
  firstName: z.string().trim().min(2, 'Nome deve ter pelo menos 2 caracteres').max(50, 'Nome muito longo'),
  lastName: z.string().trim().min(2, 'Sobrenome deve ter pelo menos 2 caracteres').max(50, 'Sobrenome muito longo'),
  phone_number: z
    .string()
    .optional()
    .nullable()
    .transform((val) => {
      if (!val || val.trim() === "") return null;
      const digits = val.replace(/\D/g, "");
      return digits || null;
    })
    .refine(
      (val) => {
        if (!val) return true;
        return val.length >= 10 && val.length <= 11;
      },
      {
        message: "O telefone deve ter entre 10 e 11 dígitos (incluindo DDD)",
      },
    ),
});

type ProfileFormData = z.infer<typeof profileSchema>;

interface ProfileFormProps {
  profile: UserProfile;
  onSubmit: (data: ProfileFormData) => void;
  isLoading: boolean;
  formId?: string;
  onDirtyChange?: (isDirty: boolean) => void;
}

export const ProfileForm: React.FC<ProfileFormProps> = ({
  profile,
  onSubmit,
  isLoading,
  formId,
  onDirtyChange,
}) => {
  const { isDemoMode } = useDemoMode();
  const { toast } = useToast();
  const [passwordOpen, setPasswordOpen] = useState(false);

  // Separar o nome completo em firstName e lastName
  const { firstName, lastName, formattedPhone } = useMemo(() => {
    const nameParts = (profile.user_name || '').split(' ');
    const phone = profile.phone_number || '';
    const formatted = phone ? formatPhoneNumber(phone) : '';
    return {
      firstName: nameParts[0] || '',
      lastName: nameParts.slice(1).join(' ') || '',
      formattedPhone: formatted,
    };
  }, [profile.user_name, profile.phone_number]);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: firstName,
      lastName: lastName,
      phone_number: formattedPhone,
    },
  });

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const passwordMutation = useMutation({
    mutationFn: async ({ newPassword }: { newPassword: string }) => {
      const userId = parseInt(profile.user_id, 10);
      await keycloakApi.updateUser(userId, { password: newPassword });
    },
    onSuccess: () => {
      setPasswordOpen(false);
      toast({ title: 'Senha alterada com sucesso' });
    },
    onError: (error: Error) => {
      toast({
        title: 'Erro ao alterar senha',
        description: error.message || 'Ocorreu um erro inesperado.',
        variant: 'destructive',
      });
    },
  });

  return (
    <div className="space-y-4">
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">Nome *</Label>
            <Input
              id="firstName"
              {...register('firstName')}
              placeholder="Nome"
            />
            {errors.firstName && (
              <p className="text-sm text-red-600">{errors.firstName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">Sobrenome *</Label>
            <Input
              id="lastName"
              {...register('lastName')}
              placeholder="Sobrenome"
            />
            {errors.lastName && (
              <p className="text-sm text-red-600">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="email">Email</Label>
            <Button
              type="button"
              variant="link"
              className="h-auto p-0 text-sm font-normal text-muted-foreground"
              onClick={() => setPasswordOpen(true)}
            >
              Alterar senha
            </Button>
          </div>
          <Input
            id="email"
            value={maskEmail(profile.email, isDemoMode)}
            disabled
            className="bg-gray-50"
          />
          <p className="text-sm text-gray-500">
            O email não pode ser alterado
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone_number">Telefone</Label>
          <Input
            id="phone_number"
            {...(isDemoMode
              ? { value: maskPhoneNumber(profile.phone_number, isDemoMode), disabled: true, className: "bg-gray-50" }
              : {
                  ...register('phone_number'),
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    const formatted = formatPhoneNumber(e.target.value);
                    setValue('phone_number', formatted, { shouldDirty: true });
                  },
                }
            )}
            placeholder="(11) 99999-9999"
            maxLength={15}
          />
          {errors.phone_number && (
            <p className="text-sm text-red-600">{errors.phone_number.message}</p>
          )}
        </div>
      </form>

      <Dialog open={passwordOpen} onOpenChange={setPasswordOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Alterar Senha</DialogTitle>
          </DialogHeader>
          <PasswordChangeForm
            onSubmit={({ newPassword }) =>
              passwordMutation.mutate({ newPassword })
            }
            isLoading={passwordMutation.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};
